/**
 * THE HUNT WORLD — mutable, non-React state that the canvas reads every frame.
 *
 * Deliberately outside React. Sixty times a second the renderer needs the herd,
 * every live effect and the shell count; routing that through component state
 * would re-render a tree sixty times a second to move a crosshair. React owns
 * the saloon, the cards and the menus, where a re-render is one per human
 * action. The canvas owns the hunt.
 *
 * Everything here is either a copy of a server message or a purely cosmetic
 * effect. Nothing in this file decides anything about score — a `kill` in the
 * feed is here because the server said so.
 */

import type { DeerSpawn, HitZone } from '@shared/deer.js';
import { poseAt } from '@shared/deer.js';
import type { VarmintRecipe, WaveKind } from '@shared/sites.js';
import { SITES, type Site } from '@shared/sites.js';
import { clock } from '../net/clock.js';

export interface Varmint extends VarmintRecipe {
  id: number;
  t0: number;
  t1: number;
}

export interface DeadDeer {
  /** Server time it was hit. */
  at: number;
  by: string;
  hue: number;
  zone: HitZone;
  points: number;
  /** Frozen pose, so a dropped deer stays where it fell. */
  sx: number;
  sy: number;
  scale: number;
  dir: number;
  isDoe: boolean;
}

export interface Tracer {
  /** Local performance.now() at creation — effects are cosmetic, so local time. */
  born: number;
  sx: number;
  sy: number;
  hue: number;
  hit: boolean;
  mine: boolean;
}

export interface Floater {
  born: number;
  sx: number;
  sy: number;
  text: string;
  hue: number;
  big: boolean;
  bad: boolean;
}

export interface FeedItem {
  born: number;
  text: string;
  hue: number;
  tone: 'kill' | 'doe' | 'miss' | 'info';
}

const TRACER_MS = 260;
const FLOATER_MS = 1500;
const FEED_MS = 4200;
const DEAD_MS = 2600;

export class HuntWorld {
  waveIndex = 0;
  kind: WaveKind = 'hunt';
  site: Site = SITES[0]!;
  startsAt = 0;
  endsAt = 0;
  active = false;

  deer: DeerSpawn[] = [];
  varmints: Varmint[] = [];
  dead = new Map<number, DeadDeer>();
  escaped = new Set<number>();
  varmintDown = new Map<number, { at: number; kind: 'critter' | 'skunk' }>();

  tracers: Tracer[] = [];
  floaters: Floater[] = [];
  feed: FeedItem[] = [];

  /** Your own gun. */
  shells = 0;
  maxShells = 0;
  reloadUntil = 0;
  benched = false;

  /** Camera kick, in stage units, decaying. */
  shake = 0;
  /** Muzzle flash intensity, 0..1, decaying. */
  flash = 0;
  /**
   * Global flash-rate governor (WCAG 2.3.1, ROUNDTABLE.md A-24). No more than
   * three flashes in any second, no matter how fast anyone fires — the varmint
   * round with eight players is the case that needs it.
   */
  private flashTimes: number[] = [];

  /** Set when the player has asked for reduced motion. */
  reducedMotion = false;

  reset() {
    this.deer = [];
    this.varmints = [];
    this.dead.clear();
    this.escaped.clear();
    this.varmintDown.clear();
    this.tracers = [];
    this.floaters = [];
    this.shells = 0;
    this.maxShells = 0;
    this.reloadUntil = 0;
    this.benched = false;
    this.active = false;
    this.shake = 0;
    this.flash = 0;
  }

  beginWave(msg: {
    waveIndex: number;
    kind: WaveKind;
    siteId: string;
    startsAt: number;
    endsAt: number;
    shells: number;
    deer: DeerSpawn[];
    varmints: Varmint[];
  }) {
    this.reset();
    this.waveIndex = msg.waveIndex;
    this.kind = msg.kind;
    this.site = SITES.find((s) => s.id === msg.siteId) ?? SITES[0]!;
    this.startsAt = msg.startsAt;
    this.endsAt = msg.endsAt;
    this.deer = msg.deer;
    this.varmints = msg.varmints;
    this.maxShells = msg.shells;
    this.active = true;
  }

  /** Deer that should be drawn right now. */
  liveDeer(t: number): DeerSpawn[] {
    return this.deer.filter((d) => t >= d.t0 && t < d.tExit && !this.dead.has(d.id) && !this.escaped.has(d.id));
  }

  liveVarmints(t: number): Varmint[] {
    return this.varmints.filter((v) => t >= v.t0 && t <= v.t1 && !this.varmintDown.has(v.id));
  }

  // ── events from the server ───────────────────────────────────────────────

  onKill(deerId: number, by: string, hue: number, zone: HitZone, points: number, rack: number, st: number, mine: boolean) {
    const d = this.deer.find((x) => x.id === deerId);
    if (d) {
      const p = poseAt(d, st);
      this.dead.set(deerId, {
        at: st,
        by,
        hue,
        zone,
        points,
        sx: p.sx,
        sy: p.sy,
        scale: p.scale,
        dir: p.dir,
        isDoe: false,
      });
      this.floaters.push({
        born: performance.now(),
        sx: p.sx,
        sy: p.sy - 0.75 * p.scale,
        text: `+${points.toLocaleString()}`,
        hue,
        big: rack >= 12,
        bad: false,
      });
    }
    const label = rack >= 20 ? `THE LEGEND — ${rack} POINTS` : `${rack}-pointer`;
    this.feed.unshift({
      born: performance.now(),
      text: mine ? `You took a ${label} · ${zoneWord(zone)}` : `${by} took a ${label}`,
      hue,
      tone: 'kill',
    });
    this.trim();
  }

  onDoe(deerId: number, by: string, hue: number, penalty: number, mine: boolean) {
    const d = this.deer.find((x) => x.id === deerId);
    if (d) {
      const p = poseAt(d, clock.now());
      this.dead.set(deerId, {
        at: clock.now(),
        by,
        hue,
        zone: 'body',
        points: -penalty,
        sx: p.sx,
        sy: p.sy,
        scale: p.scale,
        dir: p.dir,
        isDoe: true,
      });
      this.floaters.push({
        born: performance.now(),
        sx: p.sx,
        sy: p.sy - 0.75 * p.scale,
        text: `−${penalty}`,
        hue,
        big: true,
        bad: true,
      });
    }
    this.feed.unshift({
      born: performance.now(),
      // The public part of the doe rule. Everybody hears about it (A-13).
      text: mine ? 'YOU SHOT A DOE — out for this wave' : `${by} SHOT A DOE`,
      hue,
      tone: 'doe',
    });
    this.trim();
  }

  onVarmint(id: number, kind: 'critter' | 'skunk', by: string, hue: number, points: number, mine: boolean) {
    const v = this.varmints.find((x) => x.id === id);
    this.varmintDown.set(id, { at: clock.now(), kind });
    if (v) {
      this.floaters.push({
        born: performance.now(),
        sx: v.sx,
        sy: v.sy - 0.08,
        text: points >= 0 ? `+${points}` : `${points}`,
        hue,
        big: false,
        bad: points < 0,
      });
    }
    if (kind === 'skunk') {
      this.feed.unshift({
        born: performance.now(),
        text: mine ? 'You shot the skunk.' : `${by} shot the skunk`,
        hue,
        tone: 'doe',
      });
      this.trim();
    }
  }

  onEscaped(deerId: number) {
    this.escaped.add(deerId);
  }

  onShot(sx: number, sy: number, hue: number, hit: boolean, mine: boolean) {
    this.tracers.push({ born: performance.now(), sx, sy, hue, hit, mine });
    if (mine) {
      this.kick(hit ? 0.02 : 0.014);
      this.pulse();
    }
    if (this.tracers.length > 40) this.tracers.splice(0, this.tracers.length - 40);
  }

  note(text: string, tone: FeedItem['tone'] = 'info', hue = 0) {
    this.feed.unshift({ born: performance.now(), text, hue, tone });
    this.trim();
  }

  // ── effects ──────────────────────────────────────────────────────────────

  kick(amount: number) {
    if (this.reducedMotion) return;
    this.shake = Math.min(0.05, this.shake + amount);
  }

  /**
   * A muzzle flash, subject to the three-per-second global cap. Refusing the
   * fourth is not a graphical compromise; it is the difference between a game
   * and a seizure risk for a photosensitive player in a varmint round.
   */
  pulse() {
    const now = performance.now();
    this.flashTimes = this.flashTimes.filter((t) => now - t < 1000);
    if (this.flashTimes.length >= 3) return;
    this.flashTimes.push(now);
    this.flash = this.reducedMotion ? 0.25 : 1;
  }

  /** Advance cosmetic decay. `dt` is real seconds. */
  step(dt: number) {
    this.shake *= Math.pow(0.0008, dt);
    if (this.shake < 0.0005) this.shake = 0;
    this.flash *= Math.pow(0.00002, dt);
    if (this.flash < 0.01) this.flash = 0;

    const now = performance.now();
    this.tracers = this.tracers.filter((x) => now - x.born < TRACER_MS);
    this.floaters = this.floaters.filter((x) => now - x.born < FLOATER_MS);
    this.feed = this.feed.filter((x) => now - x.born < FEED_MS);

    const t = clock.now();
    for (const [id, d] of this.dead) if (t - d.at > DEAD_MS) this.dead.delete(id);
    for (const [id, v] of this.varmintDown) if (t - v.at > 900) this.varmintDown.delete(id);
  }

  private trim() {
    if (this.feed.length > 6) this.feed.length = 6;
  }

  /** 0 before the wave starts, counting down in whole seconds. */
  countdown(t: number): number {
    return t < this.startsAt ? Math.ceil((this.startsAt - t) / 1000) : 0;
  }

  /** Seconds left in the wave, for the HUD clock. */
  secondsLeft(t: number): number {
    return Math.max(0, Math.ceil((this.endsAt - t) / 1000));
  }

  reloadProgress(t: number): number {
    if (!this.reloadUntil) return 1;
    const total = 850;
    return Math.max(0, Math.min(1, 1 - (this.reloadUntil - t) / total));
  }
}

function zoneWord(zone: HitZone): string {
  return zone === 'vitals' ? 'clean kill' : zone === 'head' ? 'head shot' : 'downed';
}

export const world = new HuntWorld();

export const TRACER_LIFE_MS = TRACER_MS;
export const FLOATER_LIFE_MS = FLOATER_MS;
export const DEAD_LIFE_MS = DEAD_MS;
